import flatPriceCurrencies from '../constants/flatPriceCurrencies';

const typeLabels = { invoice: 'Invoice', proforma: 'Proforma invoice' };

const statusBadges = {
  draft: { label: 'Draft', color: 'gray' },
  issued: { label: 'Issued', color: 'green' },
  void: { label: 'Void', color: 'red' },
};

export const invoiceStatusBadge = (status) => statusBadges[status] ?? { label: status, color: 'gray' };

// Invoices created before the API grew proformas carry no type.
export const invoiceTypeOf = ({ type }) => (type === 'proforma' ? 'proforma' : 'invoice');

export const invoiceTypeLabel = (type) => typeLabels[type] ?? typeLabels.invoice;

// The API prices an invoice only in the currencies its converters know. An
// invoice that predates that list keeps its own currency as an option, so
// opening the select does not force a change.
export const invoiceCurrencyOptions = (currency) =>
  currency && !flatPriceCurrencies.includes(currency) ? [...flatPriceCurrencies, currency] : flatPriceCurrencies;

const sortValues = {
  // The API's `number` is an integer column; a draft has none yet.
  number: ({ number }) => {
    const value = number == null || number === '' ? NaN : Number(number);
    return Number.isFinite(value) ? value : null;
  },
  day: ({ day }) => day,
  // Invoices are issued in several currencies, so the only amount comparable
  // across the list is the EUR one. Invoices issued before the API stored EUR
  // totals fall back to their own currency's pre-VAT total.
  amount: ({ templateData, totalBeforeTax }) => {
    const value = Number(templateData?.totals?.eur?.baseAmount ?? totalBeforeTax);
    return Number.isFinite(value) ? value : null;
  },
};

// A missing value sorts last in BOTH directions: flipping the direction is meant
// to surface the other end of the numbered rows, not to float the drafts.
const compareSortValues = (a, b, direction) => {
  if (a === null || b === null) {
    return a === b ? 0 : a === null ? 1 : -1;
  }
  const ordering = typeof a === 'string' ? a.localeCompare(b) : a - b;
  return direction === 'desc' ? -ordering : ordering;
};

// Client-side ordering of the already-filtered list. Without a key the list is
// returned exactly as given, which is the API's id-ascending order.
export const sortInvoices = (invoices, { key, direction } = {}) => {
  const valueOf = sortValues[key];
  if (!invoices || !valueOf) {
    return invoices;
  }
  return [...invoices].sort(
    (a, b) =>
      compareSortValues(valueOf(a), valueOf(b), direction) ||
      // A proforma and an invoice are numbered from separate counters, so two
      // rows can carry the same number.
      (key === 'number' ? invoiceTypeOf(a).localeCompare(invoiceTypeOf(b)) : 0) ||
      compareSortValues(a.id, b.id, 'asc')
  );
};

const maxValueLength = 60;

const truncate = (value) => {
  const text = String(value);
  return text.length > maxValueLength ? `${text.slice(0, maxValueLength)}…` : text;
};

const lineItemLabel = ({ description, quantity, unitPrice }) => `"${description}" (${quantity} × ${unitPrice.price})`;

const lineItemKey = (item) => `${lineItemLabel(item)}|${item.domainId ?? ''}`;

const lineItemIdentity = ({ description, domainId }) => `${description}|${domainId ?? ''}`;

// Removing a line shifts every later one, so pairing by position would misname the line that went:
// identical lines cancel out first, then leftovers with the same description and domain read as changed.
const describeLineItems = (from, to) => {
  const remaining = [...(to ?? [])];
  const unmatched = [];
  (from ?? []).forEach((before) => {
    const match = remaining.findIndex((after) => lineItemKey(after) === lineItemKey(before));
    if (match === -1) {
      unmatched.push(before);
    } else {
      remaining.splice(match, 1);
    }
  });

  const changed = [];
  const removed = [];
  unmatched.forEach((before) => {
    const match = remaining.findIndex((after) => lineItemIdentity(after) === lineItemIdentity(before));
    if (match === -1) {
      removed.push(`removed ${lineItemLabel(before)}`);
    } else {
      changed.push(`changed ${lineItemLabel(before)} to ${lineItemLabel(remaining[match])}`);
      remaining.splice(match, 1);
    }
  });
  const added = remaining.map((after) => `added ${lineItemLabel(after)}`);
  return `Line items: ${[...changed, ...removed, ...added].join('; ') || 'changed'}`;
};

export const describeInvoiceChanges = (changes) =>
  Object.entries(changes).map(([rawField, { from, to }]) => {
    const field = rawField.replace(/^templateData\./, '');
    switch (field) {
      case 'type':
        return `Type: ${invoiceTypeLabel(from)} → ${invoiceTypeLabel(to)}`;
      case 'day':
        return `Date: ${from} → ${to}`;
      case 'currency':
        return `Currency: ${from} → ${to}`;
      case 'lineItems':
        return describeLineItems(from, to);
      default:
        return `${field}: ${truncate(JSON.stringify(from ?? null))} → ${truncate(JSON.stringify(to ?? null))}`;
    }
  });
